import AppCard from '@nxsys/components/AppCard';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppDescription from '@nxsys/components/AppDescription';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppLoader from '@nxsys/components/AppLoader';
import AppRateForm, {
  AppRateFormItem,
  calculateNetAndTotalFromRate,
  PAYMENT_RATE_DEFAULT,
} from '@nxsys/components/AppRateForm';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import {
  HP_CODES,
  CODE_RATE_HOLIDAY_LEAVER,
  CODE_RATE_HOLIDAY_ER_COSTS_LEAVER,
  CODE_RATE_PENSION_REFUND_LEAVER,
  CODE_RATE_PENSION_REFUND_ER_LEAVER,
  LEAVER_CODES,
} from '@nxsys/constants/AppConst';
import {
  AccrueHolidayPayEnum,
  AgencyInvoiceMethodEnum,
  HolidayMethodEnum,
} from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Option, PaymentFrequencyType } from '@nxsys/types/models/Common';
import {
  isHolidayPayRate,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import {
  getCurrentTaxPeriodByFrequency,
  getOptionsAllTaxWeekTaxMonth,
} from '@nxsys/utils/taxYear';
import { searchPaymentRate } from '@nxsys/services/search';
import { Descriptions, Form } from 'antd';
import { some } from 'lodash';
import moment from 'moment';
import { Ref, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

export type TimesheetFormValues = {
  subcontractorRef: Option<string> | null;
  assignmentRef: Option<string> | null;
  agencyRef: Option<string> | null;
  startDate: string | null;
  endDate: string | null;
  siteId: Option<string> | null;
  taxPeriod: Option<string> | null;
  file: File | string | null;
  rates?: AppRateFormItem[];
};

export type TimesheetFormRefType = {
  submit: () => void;
};

export type P45Context = {
  selectSub: any;
  holidayPaid: number;
  erCosts: number;
  eePensionRefund: number;
  erPensionRefund: number;
  isIssueP45: boolean;
  isRefundPension: boolean;
  taxPeriod?: number;
  // Navigated from "Make Payment After Leaving": pre-select the leaver + show the
  // PAL warning, but DO NOT issue a new P45 (that stays gated on isIssueP45).
  isPAL?: boolean;
};

type TimesheetFormProps = {
  formRef?: Ref<TimesheetFormRefType>;
  onSubmit?: (values: TimesheetFormValues) => void;
  defaultValues?: TimesheetFormValues;
  p45Context?: P45Context | null;
  onSubcontractorChange?: () => void;
};

const initialValues: TimesheetFormValues = {
  subcontractorRef: null,
  assignmentRef: null,
  agencyRef: null,
  startDate: null,
  endDate: null,
  siteId: null,
  taxPeriod: null,
  file: null,
};

const TimesheetForm = ({
  formRef,
  onSubmit,
  defaultValues = initialValues,
  p45Context,
  onSubcontractorChange,
}: TimesheetFormProps) => {
  const { messages } = useTypedIntl();

  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [form] = Form.useForm<TimesheetFormValues>();
  const watchValues = Form.useWatch([], form);
  const watchRates = Form.useWatch<AppRateFormItem[]>('rates', form);

  const subcontractor = useMemo(
    () => watchValues?.subcontractorRef?.fullData,
    [watchValues?.subcontractorRef],
  );
  const holidayPayMethod = useMemo(
    () =>
      subcontractor?.setting?.holidayPayMethod ??
      subcontractor?.primaryAgency?.holidayPayMethod ??
      subcontractor?.companyPayment?.holidayPayMethod,
    [subcontractor],
  );
  const holidayPayAccrualMethod = useMemo(
    () =>
      subcontractor?.setting?.holidayPayAccrualMethod ??
      subcontractor?.primaryAgency?.holidayPayAccrualMethod ??
      subcontractor?.companyPayment?.payeHolidayPayAccrualMethod,
    [subcontractor],
  );

  const agency = useMemo(
    () => watchValues?.agencyRef?.fullData,
    [watchValues?.agencyRef],
  );

  const [taxPeriodOptions, setTaxPeriodOptions] = useState<Option<string>[]>(
    [],
  );
  const [isFetchingP45Rates, setIsFetchingP45Rates] = useState(false);

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
  }));

  // Fetch leaver rate codes and pre-fill amounts for P45 timesheet
  const fetchAndApplyP45Rates = useCallback(
    async (subcontractorData: any) => {
      if (!p45Context?.isIssueP45 || !subcontractorData) return;

      setIsFetchingP45Rates(true);
      try {
        const res = await searchPaymentRate({
          page: 0,
          size: 1000,
          isIssueP45: true,
        });
        const allRates = (res as any)?.data?.data ?? [];

        const isUmbrella = ['UMBRELLA', 'HYBRID'].includes(
          subcontractorData?.setting?.paymentModel,
        );
        const paid = Number(p45Context.holidayPaid || 0);
        const paidErCosts = Number(p45Context.erCosts || 0);
        const paidEEPensionRefund = Number(p45Context.eePensionRefund || 0);
        const paidERPensionRefund = isUmbrella
          ? Number(p45Context.erPensionRefund || 0)
          : 0;

        const leaverRates = allRates
          .filter((rate: any) => LEAVER_CODES.includes(rate.code))
          .map((rate: any) => {
            let value = 0;
            if (rate.code === CODE_RATE_HOLIDAY_LEAVER) value = paid;
            else if (rate.code === CODE_RATE_HOLIDAY_ER_COSTS_LEAVER)
              value = paidErCosts;
            else if (rate.code === CODE_RATE_PENSION_REFUND_LEAVER)
              value = paidEEPensionRefund;
            else if (rate.code === CODE_RATE_PENSION_REFUND_ER_LEAVER)
              value = paidERPensionRefund;
            if (value <= 0) return null;

            const vatRate = rate.defaultVATCode?.VATRate ?? '0';
            const netTotal = calculateNetAndTotalFromRate(
              value.toString(),
              '1',
              vatRate,
            );
            return {
              rateId: rate.id || '',
              rateType: rate.type || 'RATE',
              rate: value.toString(),
              vatRate,
              description: rate.description || '',
              rateCode: { label: rate.code, value: rate.code },
              vatDetails: rate.defaultVATCode
                ? {
                    label: `${rate.defaultVATCode.description} (${rate.defaultVATCode.VATCode}) - ${rate.defaultVATCode.VATRate}`,
                    value: `${rate.defaultVATCode.description} (${rate.defaultVATCode.VATCode}) - ${rate.defaultVATCode.VATRate}`,
                  }
                : null,
              vatCode: rate.defaultVATCode?.VATCode ?? '',
              net: netTotal.net,
              total: netTotal.total,
              units: '1',
            } as AppRateFormItem;
          })
          .filter(Boolean) as AppRateFormItem[];

        if (leaverRates.length > 0) {
          form.setFieldValue('rates', leaverRates);
        }
      } catch {
        // Silently fail - user can still manually select rates
      } finally {
        setIsFetchingP45Rates(false);
      }
    },
    [p45Context, form],
  );

  useEffect(() => {
    if (defaultValues?.subcontractorRef) {
      handleSelectSubcontractor(defaultValues?.subcontractorRef, true);
    }
  }, [defaultValues]);

  const onFinish = (values: TimesheetFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  const getTaxPeriodOptionsByFrequency = (
    paymentFrequency: PaymentFrequencyType,
  ) => {
    const taxPeriodOptions = getOptionsAllTaxWeekTaxMonth(
      paymentFrequency,
    )?.map((option) => ({
      label: option.label,
      value: option.value.toString(),
    }));
    return taxPeriodOptions;
  };

  const handleSelectSubcontractor = (value: any, isAutoSelect = false) => {
    const subcontractorData = value?.fullData;
    // Skip for programmatic auto-select (e.g. PAL pre-fill from defaultValues),
    // so it doesn't reset a PAL confirmation the user already accepted.
    if (!isAutoSelect) onSubcontractorChange?.();
    const payFrequency =
      subcontractorData?.payFrequency ||
      subcontractorData?.companyPayment?.paymentFrequency;
    const _taxPeriodOptions = getTaxPeriodOptionsByFrequency(payFrequency);

    // Use P45 tax period if available, otherwise use current tax period
    const taxPeriodValue = p45Context?.taxPeriod
      ? _taxPeriodOptions.find(
          (option) =>
            option.value === p45Context.taxPeriod?.toString(),
        )
      : _taxPeriodOptions.find(
          (option) =>
            option.value ===
            getCurrentTaxPeriodByFrequency(payFrequency)?.toString(),
        );

    form.setFieldsValue({
      subcontractorRef: value,
      assignmentRef: null,
      taxPeriod: taxPeriodValue ?? null,
      rates: [PAYMENT_RATE_DEFAULT],
    });
    if (!value) form.setFieldValue('rates', [PAYMENT_RATE_DEFAULT]);
    setTaxPeriodOptions(_taxPeriodOptions);

    // Fetch and apply P45 leaver rates after subcontractor is selected
    if (p45Context?.isIssueP45 && subcontractorData) {
      fetchAndApplyP45Rates(subcontractorData);
    }
  };

  const handleSelectAssignment = (value: any) => {
    form.setFieldValue('assignmentRef', value);
    const assignmentData = value?.fullData;
    const agencyData = assignmentData?.agencyRef;
    const siteData = assignmentData?.siteRef;
    const rateData = assignmentData?.rates
      ?.map((rate: any) => ({
        ...rate,
        rateId: rate.id || rate.rateId || '',
        rateType: rate.type || rate.rateType || 'RATE',
        rateCode: {
          label: rate.rateCode,
          value: rate.rateCode,
        },
        vatDetails: {
          label: rate.vatDetails,
          value: rate.vatDetails,
        },
        ...(isHolidayPayRate(rate.rateCode) ? { units: 1 } : {}),
      }))
      ?.map((rate: any) => ({
        ...rate,
        net: calculateNetAndTotalFromRate(rate.rate, rate.units, rate.vatRate)
          .net,
        total: calculateNetAndTotalFromRate(rate.rate, rate.units, rate.vatRate)
          .total,
      }));
    if (!agency) {
      if (agencyData)
        form.setFieldValue('agencyRef', {
          label: agencyData?.name,
          value: agencyData.id,
        });
      if (siteData)
        form.setFieldValue('siteId', {
          label: siteData?.siteName,
          value: siteData.id,
        });
    }
    if (rateData && rateData.length !== 0) {
      form.setFieldValue('rates', rateData);
    }
  };

  const hasHpRate = some(watchRates, (e) => {
    const val = e?.rateCode?.value ?? '';
    const fullCode = e?.rateCode?.fullData?.code ?? '';
    return HP_CODES.includes(val) || HP_CODES.includes(fullCode);
  });

  // Only show 52-week Average Gross here — HP balance and avg hour rate are
  // already rendered inside AppRateForm to avoid duplication
  const needToShow52WeekAverageGross =
    hasHpRate &&
    subcontractor?.paymentModel === PAYMENT_MODEL_ENUM.PAYEE &&
    holidayPayMethod === HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE &&
    holidayPayAccrualMethod === AccrueHolidayPayEnum.AMOUNT;

  return (
    <div className="TimesheetForm relative">
      {isFetchingP45Rates && (
        <div className="absolute inset-0 z-10 bg-white/60">
          <AppLoader />
        </div>
      )}
      <AppCard title={messages['timesheet.addNew']}>
        <AppFormContainer
          form={form}
          initialValues={{
            ...initialValues,
            ...defaultValues,
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="px-4"
          labelCol={{ span: 6 }}
        >
          <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-x-4">
            <Form.Item
              label={messages['common.employeeName']}
              name="subcontractorRef"
              rules={[{ required: true }]}
            >
              <AppSelectSync
                placeholder={messages['form.selectEmployeeAndSubcontractor']}
                apiType="searchEmployeeSubcontractV2"
                type="emp&sub"
                onChangeValue={(value: any) =>
                  handleSelectSubcontractor(value)
                }
                disabled={!!p45Context?.isIssueP45}
              />
            </Form.Item>
            {!isBureau && (
              <>
                <Form.Item
                  label={messages['common.assignment']}
                  name="assignmentRef"
                >
                  <AppSelectSync
                    placeholder={messages['form.selectAssignment']}
                    apiType="searchAssignment"
                    type="assignment"
                    onChangeValue={handleSelectAssignment}
                  />
                </Form.Item>
                <Form.Item
                  label={messages['common.agencyName']}
                  name="agencyRef"
                  rules={[{ required: true }]}
                >
                  <AppSelectSync
                    placeholder={messages['form.selectAgency']}
                    apiType="searchAgency"
                    type="agency"
                    onChangeValue={(value: any) => {
                      form.setFieldsValue({ agencyRef: value });
                    }}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item label="Timesheet Period">
              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item name="startDate" className="mb-0">
                  <AppDatePicker
                    placeholder={messages['common.startDate']}
                    format={'DD/MM/YYYY'}
                    maxDate={
                      watchValues?.endDate
                        ? moment(watchValues.endDate)
                        : undefined
                    }
                  />
                </Form.Item>
                <Form.Item name="endDate" className="mb-0">
                  <AppDatePicker
                    placeholder={messages['common.endDate']}
                    format={'DD/MM/YYYY'}
                    minDate={
                      watchValues?.startDate
                        ? moment(watchValues.startDate)
                        : undefined
                    }
                  />
                </Form.Item>
              </div>
            </Form.Item>
            <Form.Item
              label={messages['common.siteId']}
              name="siteId"
              rules={[
                {
                  required:
                    agency?.invoicingAgencyRef?.invoicingMethod ===
                    AgencyInvoiceMethodEnum.BY_SITE,
                },
              ]}
            >
              <AppSelectSync
                placeholder={messages['form.selectSite']}
                apiType="searchSiteInAgency"
                type="agencySite"
                onChangeValue={(value: any) => {
                  form.setFieldsValue({ siteId: value });
                }}
                searchId={watchValues?.agencyRef?.value}
              />
            </Form.Item>
            <Form.Item label={messages['common.taxPeriod']} name="taxPeriod">
              <AppSelect
                options={taxPeriodOptions}
                placeholder={messages['form.selectTaxPeriod']}
              />
            </Form.Item>
            <Form.Item label={messages['common.attachment']} name="file">
              <AppUploadFile
                onChange={(file: File) => {
                  form.setFieldsValue({ file });
                }}
              />
            </Form.Item>
          </div>
        </AppFormContainer>
      </AppCard>
      {needToShow52WeekAverageGross && (
        <AppCard className="mt-4 p-4">
          <AppDescription>
            <Descriptions.Item label={messages['common.averageGross']}>
              {formatMoney(
                subcontractor?.average52WeekData?.averageGross ?? 0,
                showCurrencyByCountry(payrollCountry),
              )}
            </Descriptions.Item>
          </AppDescription>
        </AppCard>
      )}
      <AppRateForm
        className="mt-4"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        defaultValues={initialValues}
        listDisabled={
          !subcontractor
            ? [
                'rateCode',
                'rateType',
                'total',
                'units',
                'vatDetails',
                'vatRate',
                'description',
                'rate',
              ]
            : holidayPayMethod === HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE &&
                holidayPayAccrualMethod === AccrueHolidayPayEnum.AVG_HOURLY_RATE
              ? ['units']
              : []
        }
        isInvoice={false}
        agency={agency}
        subcontractor={subcontractor}
      />
    </div>
  );
};

export default TimesheetForm;
